'use client';

import { Button } from '@/components/ui/button';
import { useNotifications } from '@/hooks/useNotifications';
import { Bell, BellOff } from 'lucide-react';

interface NotificationToggleProps {
  onTokenReceived?: (token: string) => void;
  compact?: boolean;
}

export default function NotificationToggle({ onTokenReceived, compact = false }: NotificationToggleProps) {
  const { permission, token, isLoading, isIOSNonPWA, requestPermission } = useNotifications();

  if (isIOSNonPWA) {
    return null;
  }

  const handleToggle = async () => {
    if (permission === 'granted') return;

    const receivedToken = await requestPermission();
    if (receivedToken && onTokenReceived) {
      onTokenReceived(receivedToken);
    }
  };

  if (compact) {
    return (
      <Button
        onClick={handleToggle}
        disabled={isLoading || permission === 'denied'}
        variant={permission === 'granted' ? 'green' : 'outline'}
        className='h-8 w-8 p-0'
        isLoading={isLoading}>
        {permission === 'granted' ? <Bell className='h-4 w-4' /> : <BellOff className='h-4 w-4' />}
      </Button>
    );
  }

  if (permission === 'granted' && token) {
    return (
      <div className='flex items-center gap-2 text-sm text-emerald-700'>
        <Bell className='h-4 w-4' />
        <span>Notificações ativas</span>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className='flex items-center gap-2 text-sm text-red-600'>
        <BellOff className='h-4 w-4' />
        <span>Notificações bloqueadas</span>
      </div>
    );
  }

  return (
    <Button onClick={handleToggle} disabled={isLoading} variant='outline' className='h-8 text-xs' isLoading={isLoading}>
      <Bell className='mr-1 h-3 w-3' />
      {isLoading ? 'Ativando...' : 'Ativar notificações'}
    </Button>
  );
}
